import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import each from 'lodash/each';
import find from 'lodash/find';
import intersection from 'lodash/intersection';
import keys from 'lodash/keys';
import size from 'lodash/size';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Button, Divider, makeStyles } from '@material-ui/core';

import MultiSelectMenu from '../MultiSelectMenu';
import { getI18n } from "../../../utils/intl";
import {
  API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API,
  API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG,
} from '../../../constants/filter';

import FormTextField from "../../FormTextField";
import FormSelect from '../../FormSelect';

import styles from './styles';

const SHOW_ALL = 'SHOW_ALL';
const SHOW_SELECTED = 'SHOW_SELECTED';

const rankTypes = [
  {
    uuid: '0',
    text: 'Overall Rank',
    value: API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK,
  },
  {
    uuid: '1',
    text: 'Rank for API',
    value: API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API,
  },
  {
    uuid: '2',
    text: 'Rank for Organization',
    value: API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG,
  },
];

const FilterByRankAPIPerOrg = (props) => {
  const {
    updatedFilterRankType, selectedItems = {}, disableRankSelection,
    maxAllowedEntities, disableItemSelection, updatedFilterRankSize, filterData,
    updateRankTypeSizeAndSelectedItmes, isInvalidRankSize, hideCount,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    searchText: '',
    visilbilityFilter: SHOW_ALL,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const getMenuItem = (val, key) => ({ name: val.name, label: val.name, value: key });

  const getEntityFilteredData = () => {
    const PRIMARY_ID = 'primaryId';
    const PRIMARY_NAME = 'name';
    const SECONDARY_ID = 'secondaryId';
    const SECONDARY_NAME = 'secondaryName';
    let selectedEntityId;
    let selectedEntityName;
    if (updatedFilterRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
      selectedEntityId = SECONDARY_ID;
      selectedEntityName = SECONDARY_NAME;
    } else if (updatedFilterRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
      selectedEntityId = PRIMARY_ID;
      selectedEntityName = PRIMARY_NAME;
    }
    const entityFilteredData = {};
    each(filterData, (val) => {
      const item = {};
      item[PRIMARY_ID] = val[selectedEntityId];
      item[PRIMARY_NAME] = val[selectedEntityName];
      entityFilteredData[val[selectedEntityId]] = item;
    });
    return entityFilteredData;
  }

  const getMenuData = () => {
    const menuData = [];
    const entityFilteredData = getEntityFilteredData();
    each(entityFilteredData, (val, key) => {
      const searchText = state.searchText.trim();
      if (state.visilbilityFilter === SHOW_SELECTED) {
        if (selectedItems[key]) {
          if (shouldIncludeItem(val, searchText)) {
            menuData.push(getMenuItem(val, key));
          }
        }
      } else if (shouldIncludeItem(val, searchText)) {
        menuData.push(getMenuItem(val, key));
      }
    });
    return menuData;
  };

  const shouldIncludeItem = (item, searchText) => {
    const { name } = item;
    const searchTextLC = searchText.toLowerCase();
    const nameLC = name && name.toLowerCase();
    if ((searchTextLC.length < 3) || (nameLC.includes(searchTextLC))) {
      return true;
    }
    return false;
  };

  const incRankSize = () => {
    let rankSize = updatedFilterRankSize;
    const patt1 = new RegExp('^(?:[1-9]|0[1-9]|10)$');
    const patt2 = new RegExp('[!@#$%^&*(),.?":{}|<>a-z.+=`]');
    const containsChar = patt2.test(rankSize);
    let isInvalid = false;
    rankSize =
      rankSize !== maxAllowedEntities && !containsChar && !isNaN(parseInt(rankSize, 10))
        ? rankSize + 1
        : rankSize;
    if (!rankSize || (rankSize && !patt1.test(rankSize)) || (rankSize > maxAllowedEntities)) {
      isInvalid = true;
    } else {
      isInvalid = false;
    }
    updateRankTypeSizeAndSelectedItmes(
      rankSize,
      updatedFilterRankType,
      isInvalid,
      selectedItems,
    );
  };

  const decRankSize = () => {
    let rankSize = updatedFilterRankSize;
    const patt1 = new RegExp('^(?:[1-9]|0[1-9]|10)$');
    const patt2 = new RegExp('[!@#$%^&*(),.?":{}|<>a-z.+=`]');
    const containsChar = patt2.test(rankSize);
    let isInvalid = false;
    rankSize =
      rankSize !== 1 && !containsChar && !isNaN(parseInt(rankSize, 10))
        ? rankSize - 1
        : rankSize;
    if (
      !rankSize ||
      (rankSize && !patt1.test(rankSize)) ||
      rankSize > maxAllowedEntities ||
      rankSize < 0
    ) {
      isInvalid = true;
    }
    if (rankSize <= maxAllowedEntities && !rankSize < 0) {
      isInvalid = false;
    }
    updateRankTypeSizeAndSelectedItmes(
      rankSize,
      updatedFilterRankType,
      isInvalid,
      selectedItems,
    );
  };

  const handleVisibilityFilterToggle = () => {
    const visilbilityFilter =
      state.visilbilityFilter === SHOW_ALL ? SHOW_SELECTED : SHOW_ALL;
    setState({
      visilbilityFilter,
    });
  };

  const handleRankSizeInputChange = event => {
    const patt1 = new RegExp('^(?:[1-9]|0[1-9]|10)$');
    const rankSize =
      isNaN(event.target.value) || !event.target.value
        ? event.target.value
        : parseInt(event.target.value, 10);
    let isInvalid = false;
    if (
      !event.target.value ||
      (event.target.value && !patt1.test(event.target.value)) ||
      rankSize > maxAllowedEntities
    ) {
      isInvalid = true;
    }
    updateRankTypeSizeAndSelectedItmes(
      rankSize,
      updatedFilterRankType,
      isInvalid,
      selectedItems,
    );
  };

  const handlePrimarySearch = value => {
    setState({
      searchText: value,
    });
  };

  const handleOnSelect = (value, checked) => {
    if (checked) {
      selectedItems[value] = true;
    } else if (selectedItems[value]) {
      delete selectedItems[value];
    }
    const invalidSelection = size(selectedItems) > maxAllowedEntities;
    let selectedItemsIds = keys(selectedItems);
    const allIds = keys(getEntityFilteredData());
    selectedItemsIds = intersection(selectedItemsIds, allIds);
    const updatedSelectedItems = {};
    each(selectedItemsIds, item => { updatedSelectedItems[item] = true; });
    updateRankTypeSizeAndSelectedItmes(
      updatedFilterRankSize,
      updatedFilterRankType,
      invalidSelection,
      updatedSelectedItems,
    );
  };

  const handleRankTypeChange = (evt) => {
    const selectedRankTypeId = evt.target.value;
    const selectedRankTypeItem = find(rankTypes, item => item.uuid === selectedRankTypeId);
    updateRankTypeSizeAndSelectedItmes(
      updatedFilterRankSize,
      selectedRankTypeItem.value,
      false,
      {},
    );
  };

  const rankSize = updatedFilterRankSize;
  let remainingSlots = maxAllowedEntities;
  if (!isInvalidRankSize && Number.isInteger(rankSize)) {
    remainingSlots =
      maxAllowedEntities - rankSize < 0 || rankSize < 0
        ? maxAllowedEntities
        : maxAllowedEntities - rankSize;
  }
  const selectedItem = find(rankTypes, item => item.value === updatedFilterRankType);
  const showEntitySelector = updatedFilterRankType !== API_PER_ORG_RANK_FILTER_TYPE_OVER_ALL_RANK;
  const menuData = getMenuData();
  let placeHolderText = '';
  if (updatedFilterRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_API) {
    placeHolderText = intl.getI18nMessage('label.analyticsDashboard.filter.dialog.search.placeholder.api');
  } else if (updatedFilterRankType === API_PER_ORG_RANK_FILTER_TYPE_RANK_FOR_ORG) {
    placeHolderText = intl.getI18nMessage('label.analyticsDashboard.filter.dialog.search.placeholder.org');
  }

  return (
    <div className={classes.filterRankContainer}>
      <div className={classes.fieldsContainer}>
        <div className={classes.rankFieldsContainer}>
          <div>
            <div className={classes.label}>
              {intl.getI18nMessage('label.analyticsDashboard.filter.dialog.apiperorg.rank.label.ranksize')}
            </div>
            <div className={classes.rankSizeSection}>
              <FormTextField
                fieldContainerClass={classes.rankField}
                className={clsx(
                  classes.textInput,
                  state.invalidInput ? classes.invalidTextInput : null,
                )}
                disabled={disableRankSelection}
                value={rankSize}
                onChange={handleRankSizeInputChange}
              />
              <button
                className={classes.rankSizeBtn}
                data-apim-test={'rank-plus-btn'}
                disabled={disableRankSelection}
                onClick={incRankSize}
              >
                ＋
              </button>
              <button
                className={classes.rankSizeBtn}
                data-apim-test={'rank-minus-btn'}
                disabled={disableRankSelection}
                onClick={decRankSize}
              >
                －
              </button>
            </div>
          </div>
          <div>
            <div className={classes.label}>
              {intl.getI18nMessage('label.analyticsDashboard.filter.dialog.apiperorg.rank.label.ranktype')}
            </div>
            <div>
              <FormSelect
                fieldContainerClass={classes.rankTypeSelectContainer}
                noNoneOption
                name={'select'}
                data={rankTypes}
                hideLabel
                size={'large'}
                value={selectedItem.uuid || rankTypes[0].uuid}
                handleChange={handleRankTypeChange}
              />
            </div>
          </div>
        </div>
        {showEntitySelector && <div className={classes.searchBar}>
          <FormTextField
            placeholder={placeHolderText}
            handleChange={handlePrimarySearch}
            value={state.searchText}
          />
          <div className={classes.linkBar}>
            <Button
              className={classes.linkBtn}
              onClick={handleVisibilityFilterToggle}
            >
              {state.visilbilityFilter === SHOW_ALL ? intl.getI18nMessage(
                'label.analyticsDashboard.multiSelect.label.showSelected',
              ) : intl.getI18nMessage(
                'label.analyticsDashboard.multiSelect.label.showAll',
              )}
            </Button>
          </div>
          <Divider className={classes.menuDivider} />
          <MultiSelectMenu
            data={menuData}
            disabled={disableItemSelection}
            selectedData={selectedItems}
            handleOnSelect={handleOnSelect}
          />
        </div>}
      </div>
      {hideCount ? null : (
        <div className={classes.helperMessage}>
          {' '}
          {remainingSlots}/{maxAllowedEntities} data slots remaining
        </div>
      )}
      {state.invalidInput ? (
        <p className={classes.errorMessage}>
          {intl.getI18nMessage('label.analytics.filter.filterByRank.errorMessage.text')}
        </p>
      ) : null}
    </div>
  );

}

FilterByRankAPIPerOrg.propTypes = {
  isInvalidRankSize: PropTypes.bool,
  updateRankTypeSizeAndSelectedItmes: PropTypes.func,
  updatedFilterRankSize: PropTypes.number,
  updatedFilterRankType: PropTypes.string,
  maxAllowedEntities: PropTypes.number,
  hideCount: PropTypes.bool,
  filterData: PropTypes.object,
  selectedItems: PropTypes.object,
  disableRankSelection: PropTypes.bool,
  disableItemSelection: PropTypes.bool,
};

export default FilterByRankAPIPerOrg;
